import React, { useState, useEffect } from "react";
import { Button, Form, InputNumber, Modal, Select, Space, Switch, Table, Tag, Tooltip, Transfer, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useNotifications } from "../../../../../context/NotificationContext";
import {
	fetchTandemRules,
	createTandemRule,
	updateTandemRule,
	deleteTandemRule,
	toggleTandemRuleActive,
} from "../../../../../api/healthcare/Appointment/AppointmentSettingsApi";
import { TandemRule, TandemRuleCreateRequest } from "../../../../../types/appointments/AppointmentSettingsTypes";
import InlineLoading from "../../../../../components/shared/InlineLoading";
import EmptyState from "../../../../../components/shared/EmptyState";
import type { UserUuid } from "../../../../../types/identity";

const { Text } = Typography;
const { confirm } = Modal;

interface TandemRulesTabProps {
	appointmentTypeId: number;
	assignedPractitioners: { id: UserUuid; first_name: string; last_name: string }[];
}

const POSITION_LABELS: Record<string, string> = {
	before: "Before primary",
	during: "Concurrent with primary",
	after: "After primary",
};

const defaultForm: TandemRuleCreateRequest = {
	timing_position: "during",
	timing_minutes: 0,
	practitioner_pool: [],
	auto_assign: true,
	is_active: true,
};

const TandemRulesTab: React.FC<TandemRulesTabProps> = ({ appointmentTypeId, assignedPractitioners }) => {
	const [rules, setRules] = useState<TandemRule[]>([]);
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [editingRule, setEditingRule] = useState<TandemRule | null>(null);
	const [saving, setSaving] = useState(false);
	const [form] = Form.useForm<TandemRuleCreateRequest>();
	const [poolKeys, setPoolKeys] = useState<string[]>([]);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadRules();
	}, [appointmentTypeId]);

	const loadRules = async () => {
		setLoading(true);
		try {
			const data = await fetchTandemRules(appointmentTypeId);
			setRules(data);
		} catch {
			showError("Failed to load tandem rules");
		} finally {
			setLoading(false);
		}
	};

	const openModal = (rule?: TandemRule) => {
		if (rule) {
			setEditingRule(rule);
			form.setFieldsValue({
				timing_position: rule.timing_position,
				timing_minutes: rule.timing_minutes,
				auto_assign: rule.auto_assign,
				is_active: rule.is_active,
			});
			// A backfill orphan (a legacy integer) is not a selectable key and would 422 on the next save.
			const knownKeys = new Set(assignedPractitioners.map(p => String(p.id)));
			setPoolKeys(rule.practitioner_pool.map(String).filter(key => knownKeys.has(key)));
		} else {
			setEditingRule(null);
			form.setFieldsValue(defaultForm);
			setPoolKeys([]);
		}
		setModalVisible(true);
	};

	const handleSave = async () => {
		try {
			const values = await form.validateFields();
			const data: TandemRuleCreateRequest = { ...values, practitioner_pool: poolKeys };

			setSaving(true);
			if (editingRule) {
				const updated = await updateTandemRule(appointmentTypeId, editingRule.id, data);
				setRules(prev => prev.map(r => (r.id === updated.id ? updated : r)));
				showSuccess("Tandem rule updated");
			} else {
				const created = await createTandemRule(appointmentTypeId, data);
				setRules(prev => [...prev, created]);
				showSuccess("Tandem rule created");
			}
			setModalVisible(false);
		} catch (err: any) {
			if (err?.errorFields) return;
			showError(err?.response?.data?.message || "Failed to save tandem rule");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = (rule: TandemRule) => {
		confirm({
			title: "Delete tandem rule?",
			content: "This will remove the auto-assignment rule for this appointment type.",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteTandemRule(appointmentTypeId, rule.id);
					setRules(prev => prev.filter(r => r.id !== rule.id));
					showSuccess("Tandem rule deleted");
				} catch {
					showError("Failed to delete tandem rule");
				}
			},
		});
	};

	const handleToggleActive = async (rule: TandemRule) => {
		try {
			const updated = await toggleTandemRuleActive(appointmentTypeId, rule.id);
			setRules(prev => prev.map(r => (r.id === updated.id ? updated : r)));
		} catch {
			showError("Failed to update tandem rule status");
		}
	};

	const columns = [
		{
			title: "Position",
			dataIndex: "timing_position",
			render: (pos: string, record: TandemRule) => (
				<div>
					<Text>{POSITION_LABELS[pos]}</Text>
					{record.timing_minutes > 0 && (
						<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
							{record.timing_minutes} min {pos === "before" ? "before" : pos === "after" ? "after" : "offset"}
						</Text>
					)}
				</div>
			),
		},
		{
			title: "Practitioner Pool",
			dataIndex: "practitioner_pool",
			render: (pool: UserUuid[]) => (
				<Text type="secondary">{pool.length} practitioner{pool.length !== 1 ? "s" : ""}</Text>
			),
		},
		{
			title: "Auto-Assign",
			dataIndex: "auto_assign",
			render: (val: boolean) =>
				val ? <Tag color="blue">Auto</Tag> : <Tag>Manual</Tag>,
		},
		{
			title: "Active",
			dataIndex: "is_active",
			render: (val: boolean, record: TandemRule) => (
				<Switch size="small" checked={val} onChange={() => handleToggleActive(record)} />
			),
		},
		{
			title: "",
			key: "actions",
			width: 80,
			render: (_: any, record: TandemRule) => (
				<Space>
					<Tooltip title="Edit">
						<Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)} />
					</Tooltip>
					<Tooltip title="Delete">
						<Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
					</Tooltip>
				</Space>
			),
		},
	];

	const practitionerDataSource = assignedPractitioners.map(p => ({
		key: String(p.id),
		title: `${p.first_name} ${p.last_name}`,
	}));

	return (
		<div>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
				<div>
					<Text strong style={{ fontSize: 14 }}>Tandem Assignment Rules</Text>
					<div style={{ marginTop: 4 }}>
						<Text type="secondary" style={{ fontSize: 12 }}>
							Automatically assign a second practitioner from a selected pool when this appointment type is booked
						</Text>
					</div>
				</div>
				<Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
					Add Rule
				</Button>
			</div>

			{loading ? (
				<InlineLoading minHeight={160} message="Loading tandem rules…" />
			) : rules.length === 0 ? (
				<EmptyState
					title="No tandem rules"
					description='Click "Add Rule" to configure automatic secondary practitioner assignment'
					action={{ label: "Add Rule", icon: <PlusOutlined />, onClick: () => openModal() }}
				/>
			) : (
				<Table
					dataSource={rules}
					columns={columns}
					rowKey="id"
					size="small"
					pagination={false}
				/>
			)}

			<Modal
				title={editingRule ? "Edit Tandem Rule" : "Add Tandem Rule"}
				open={modalVisible}
				onCancel={() => setModalVisible(false)}
				onOk={handleSave}
				okText={editingRule ? "Update" : "Create"}
				confirmLoading={saving}
				width={600}
			>
				<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
					<Form.Item
						label="Timing Position"
						name="timing_position"
						rules={[{ required: true }]}
					>
						<Select
							options={[
								{ value: "before", label: "Before primary practitioner" },
								{ value: "during", label: "Concurrent with primary practitioner" },
								{ value: "after", label: "After primary practitioner" },
							]}
						/>
					</Form.Item>

					<Form.Item
						label="Offset Minutes"
						name="timing_minutes"
						tooltip="Minutes before/after/from start of the primary appointment slot (0 = same time)"
						rules={[{ required: true }]}
					>
						<InputNumber min={0} step={5} style={{ width: "100%" }} />
					</Form.Item>

					<Form.Item label="Auto-Assign" name="auto_assign" valuePropName="checked">
						<Switch checkedChildren="Auto" unCheckedChildren="Manual" />
					</Form.Item>

					<Form.Item label="Active" name="is_active" valuePropName="checked">
						<Switch />
					</Form.Item>

					<Form.Item label={<span><TeamOutlined /> Practitioner Pool</span>} required>
						<div style={{ marginBottom: 8 }}>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Select practitioners who can be auto-assigned as secondary for this rule (from those already assigned to this appointment type)
							</Text>
						</div>
						{assignedPractitioners.length === 0 ? (
							<Text type="warning">No practitioners assigned to this appointment type yet. Assign practitioners first.</Text>
						) : (
							<Transfer
								dataSource={practitionerDataSource}
								targetKeys={poolKeys}
								onChange={keys => setPoolKeys(keys as string[])}
								render={item => item.title}
								titles={["Available", "In Pool"]}
								showSearch
								style={{ width: "100%" }}
								listStyle={{ width: "100%", height: 200 }}
							/>
						)}
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default TandemRulesTab;
